/* BEGIN_COPYRIGHT_HEADER

Copyright Vspry International Limited (c) 2020
All rights reserved.

END_COPYRIGHT_HEADER */

import { useState } from 'react'
import styled from 'styled-components'
import { useFormValidation, useFunctionState } from 'vspry-hooks'
import { Button, ErrorSwal, FlexBox, Modal, TextInput, Tile } from 'vspry-style-components'

import { createConsumerBill } from 'api/bills'
import { isHolding } from 'api/common'

import { useAppConfig } from 'context/appConfigContext'
import { useAuth } from 'context/authContext'
import { useLocale } from 'context/localeContext'

import theme from 'styles/theme'

import { formatCurrencyField } from 'utils/formatters'

import HeadingText from 'components/HeadingText'
import QRScanner from 'components/QRScanner'

export const ModalFlex = styled(FlexBox)`
    @media (max-width: ${screens.medium}px) {
        max-width: 100%;
    }

    flex-direction: column;
    max-width: 420px;
    width: 420px;
`

type BillQRScannerProps = {
    onClose: () => void
}
export default function BillQRScanner({ onClose }: BillQRScannerProps) {
    const { appConfig } = useAppConfig()
    const { user, account, currentProduct } = useAuth()
    const { translate, translateErrorCode } = useLocale()
    const [{ amount, description, merchantHoldingID }, setState] = useFunctionState({ amount: '', description: '', merchantHoldingID: '' })
    const [validated, setValidated] = useState(false)
    const [submitting, setSubmitting] = useState(false)

    const { validator, onFieldValidate } = useFormValidation(setValidated)

    const handleScan = async (c: string) => {
        if (!c.match(/\d{18}/)?.length) return
        setTimeout(() => setState({ merchantHoldingID: c }), 500)
    }

    const handleSubmit = async () => {
        if (!account || !user || !currentProduct || !isHolding(currentProduct)) return
        setSubmitting(true)
        const res = await createConsumerBill({
            accountID: account.id,
            contactID: user.id,
            amount: formatCurrencyField(amount.trim(), currentProduct.currencyMultiplier),
            description: description.trim(),
            merchantHoldingID: merchantHoldingID.trim(),
        })

        if (!res?.success) {
            ErrorSwal.fire({
                title: translate('swal.error.title'),
                text: translateErrorCode(res?.message),
            })
        }
        setSubmitting(false)
        onClose()
    }

    return (
        <Modal open onClose={onClose} closeOnDimmerClick>
            <Tile>
                <ModalFlex gap='small' padding='medium'>
                    {!merchantHoldingID && (
                        <>
                            <HeadingText>{translate('forms.createbill.scanMerchantQRCode')}</HeadingText>
                            <QRScanner onScan={handleScan} />
                        </>
                    )}
                    {merchantHoldingID && (
                        <>
                            <HeadingText>{translate('forms.createbill.consumer.title')}</HeadingText>
                            <TextInput
                                id='input-field-amount'
                                label={translate('inputs.amount')}
                                placeholder='0'
                                prefix={
                                    isHolding(currentProduct) ? currentProduct?.currencySymbol ?? appConfig.base_currency : appConfig.base_currency
                                }
                                value={amount}
                                handleChange={(v) => setState({ amount: v })}
                                validator={(v) => validator.isNumeric(v)}
                                setValidated={onFieldValidate('amount')}
                            />
                            <TextInput
                                id='input-field-description'
                                label={translate('inputs.description')}
                                placeholder={translate('placeholders.description')}
                                value={description}
                                handleChange={(v) => setState({ description: v })}
                                validator={(v) => validator.isAlphanumeric(v)}
                                setValidated={onFieldValidate('description')}
                            />
                        </>
                    )}
                    <FlexBox gap='small'>
                        <Button id='billscanner-close-button' onClick={onClose} $fitted customColor={`${theme.disabledButtonBG}40`} textColor='text'>
                            {translate('buttons.close')}
                        </Button>
                        {merchantHoldingID && (
                            <Button id='billscanner-submit-button' onClick={handleSubmit} $fitted disabled={!validated || submitting}>
                                {translate(submitting ? 'buttons.submitting' : 'buttons.submit')}
                            </Button>
                        )}
                    </FlexBox>
                </ModalFlex>
            </Tile>
        </Modal>
    )
}
